import { useTranslation } from 'react-i18next'

import type { ProjectMemberRow } from '@/features/projects/types'

export interface MemberSelectProps {
  id: string
  /** The project's already-loaded roster (`selectProjectMembers`) — never re-fetched here. */
  members: readonly ProjectMemberRow[]
  value: string
  onChange: (value: string) => void
  required?: boolean
  className?: string
  'aria-label'?: string
}

/**
 * The one member picker this feature uses wherever a form asked for a raw
 * user id (deferred-findings AC-3/AC-7 — the create-plan form's two owners
 * and the reconciliation panel's assign-owner act, batch scope addition
 * comment). A native `<select>`, matching `GoNoGoPanel`'s verdict select —
 * this project ships no shadcn `Select`. Rows without a `userId` are
 * omitted: nothing here can assign to them.
 */
export function MemberSelect({
  id,
  members,
  value,
  onChange,
  required = false,
  className = '',
  'aria-label': ariaLabel
}: MemberSelectProps) {
  const { t } = useTranslation('projectWorkspace')
  const pickable = members.filter((m): m is ProjectMemberRow & { userId: string } => m.userId !== undefined)

  return (
    <select
      id={id}
      aria-label={ariaLabel}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      className={`h-9 rounded-md border border-input bg-card px-2 text-sm text-foreground ${className}`}
    >
      <option value="">{t('migration.plans.form.chooseMember')}</option>
      {pickable.map((m) => (
        <option key={m.userId} value={m.userId}>
          {m.name ?? m.userId}
        </option>
      ))}
    </select>
  )
}
